import { useSyncExternalStore, useCallback } from 'react';

/**
 * Whether this phone can be offered the app, and the one chance to offer it.
 *
 * The state lives in the module rather than in a component for two reasons.
 *
 * beforeinstallprompt fires once, early, and cannot be asked for again. A
 * component that mounts after it has fired has missed it for the life of the
 * page -- so the listener is attached at import, before React renders anything,
 * and holds the event for whoever asks later.
 *
 * And two places ask: the banner, and the item in the account menu. If each
 * kept its own copy, installing from one would leave the other still offering
 * it. One store, both subscribed.
 */

const DISMISSED_KEY = 'install_prompt_dismissed';

let deferred = null;
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

const readDismissed = () => {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    // A private window cannot remember it. Showing the offer is the better
    // failure: worst case it is dismissed again this visit.
    return false;
  }
};

let dismissed = readDismissed();

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Chrome shows its own banner otherwise, at a moment of its choosing.
    e.preventDefault();
    deferred = e;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    notify();
  });
}

export const isIos = () =>
  /iphone|ipad|ipod/i.test(window.navigator.userAgent)
  && !/crios|fxios/i.test(window.navigator.userAgent);

// Already installed: launched from the home screen rather than a browser tab.
export const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches
  || window.navigator.standalone === true;

const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

// One string covering both halves of the state, so a change to either is a
// change to the snapshot. Returning an object here would be a new identity on
// every call and would loop.
const snapshot = () => `${deferred ? 1 : 0}:${dismissed ? 1 : 0}`;

export const useInstallApp = () => {
  useSyncExternalStore(subscribe, snapshot, () => '0:1');

  const setDismissed = useCallback((value) => {
    dismissed = value;
    try {
      if (value) localStorage.setItem(DISMISSED_KEY, '1');
      else localStorage.removeItem(DISMISSED_KEY);
    } catch { /* the choice still holds for this visit */ }
    notify();
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return 'unavailable';
    const event = deferred;
    // Cleared whatever the answer is: the event is spent either way, and a
    // button that silently does nothing is worse than no button.
    deferred = null;
    notify();

    event.prompt();
    let outcome = 'dismissed';
    try {
      ({ outcome } = await event.userChoice);
    } catch { /* treated as declined */ }

    // Only an accepted install stops the offer for good.
    //
    // This used to dismiss on the way out regardless, so opening Chrome's
    // dialog and pressing Cancel silenced the banner on every future visit --
    // permanently, from one tap, on the strength of a "not right now". Chrome
    // fires a fresh beforeinstallprompt on the next load; the flag was what
    // threw it away.
    if (outcome === 'accepted') setDismissed(true);
    return outcome;
  }, [setDismissed]);

  return {
    // Something real is behind the button on this phone.
    canPrompt: deferred !== null,
    dismissed,
    installed:  isStandalone(),
    needsShare: isIos(),
    promptInstall,
    dismiss:   () => setDismissed(true),
    undismiss: () => setDismissed(false),
  };
};

export default useInstallApp;
